import React, { Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { getAPI } from '../../../Base/API';
import { isSafeImageUrl } from '../templateUtils';

/**
 * Shared "choose from hotel images" trigger button + picker modal.
 * Used by the image element inspector and the page background field.
 * Calls props.onSelect(url) with the chosen image URL.
 */
class HotelImagePicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pickerOpen: false,
            hotelImages: null,   // null = not yet loaded; [] = loaded empty
            loading: false,
            loadError: false,
        };
        // Guards the async getAPI callback against setState-after-unmount (the picker can be
        // closed / inspector unmounted while the hotel-images request is still in flight).
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _openPicker = () => {
        this.setState({ pickerOpen: true });
        // Fetch once and cache in component state (only if not already loaded).
        if (this.state.hotelImages === null && !this.state.loading) {
            this._fetchHotelImages();
        }
    };

    _closePicker = () => {
        this.setState({ pickerOpen: false });
    };

    _fetchHotelImages = () => {
        this.setState({ loading: true, loadError: false });

        getAPI(result => {
            // Bail out if the picker was unmounted while the request was in flight.
            if (!this._isMounted) return;

            const { data, error } = result;

            if (error || !data || (data.errors && data.errors.length > 0)) {
                this.setState({ loading: false, loadError: true });
                return;
            }

            const images = Array.isArray(data.response) ? data.response : [];
            this.setState({ loading: false, hotelImages: images });
        }, '/api/Voucher/Config/v1/templateAssistant/hotelImages');
    };

    _retryFetch = () => {
        this._fetchHotelImages();
    };

    _selectImage = (url) => {
        this.props.onSelect(url);
        this._closePicker();
    };

    _renderPickerBody = () => {
        const { hotelImages, loading, loadError } = this.state;

        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#6c757d' }}>
                    <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true" />
                    <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Loading" />
                </div>
            );
        }

        if (loadError) {
            return (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#842029' }}>
                    <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
                    <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Error" />
                    <div style={{ marginTop: 10 }}>
                        <Button size="sm" color="secondary" onClick={this._retryFetch}>
                            <i className="fa-solid fa-rotate-right mr-1" aria-hidden="true" />
                            <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Retry" />
                        </Button>
                    </div>
                </div>
            );
        }

        // Loaded: filter to safe URLs only (defense in depth).
        const safeImages = (hotelImages || []).filter(img => img && isSafeImageUrl(img.url));

        if (safeImages.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#6c757d' }}>
                    <i className="fa-regular fa-image mr-2" aria-hidden="true" />
                    <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Empty" />
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 10,
                }}
            >
                {safeImages.map(img => (
                    <button
                        key={img.id}
                        type="button"
                        onClick={() => this._selectImage(img.url)}
                        title={img.altText || ''}
                        style={{
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            padding: 4,
                            background: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <img
                            src={img.url}
                            alt={img.altText || ''}
                            style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 3 }}
                        />
                        {img.altText && (
                            <span
                                style={{
                                    fontSize: '0.72rem',
                                    color: '#495057',
                                    width: '100%',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {img.altText}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        );
    };

    render() {
        const { pickerOpen } = this.state;

        return (
            <Fragment>
                <Button size="sm" color="secondary" block onClick={this._openPicker}>
                    <i className="fa-regular fa-images mr-1" aria-hidden="true" />
                    <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Choose" />
                </Button>

                <Modal isOpen={pickerOpen} toggle={this._closePicker} fade={false} size="lg" centered>
                    <ModalHeader toggle={this._closePicker}>
                        <FormattedMessage id="TemplateDesigner.Inspector.HotelImages.Title" />
                    </ModalHeader>
                    <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {this._renderPickerBody()}
                    </ModalBody>
                </Modal>
            </Fragment>
        );
    }
}

export default HotelImagePicker;
